import { ContentCard } from "@msuite/ui/content-card";
import { Select, TextInput } from "@msuite/ui/form";
import { useAutoSave } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { SettingsItem } from "@msuite/ui/settings";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type ProjectSettingsFormSchema, projectSettingsFormSchema } from "@msuite/utils/schemas";
import { getUnitTranslationKey } from "@msuite/utils/strings";
import { dayUnits } from "@msuite/utils/units";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
type ProjectSettingsFormProps =
	| {
			scope: "project";
			projectId: string;
	  }
	| {
			scope: "default";
	  };

export function ProjectSettingsForm(props: ProjectSettingsFormProps) {
	const query = {
		scoped: props.scope !== "default" ? "true" : "false",
		table: props.scope === "project" ? "projectSettings" : undefined,
		identifier: props.scope === "project" ? props.projectId : undefined,
	} as const;

	const methods = useForm({
		resolver: resolver(projectSettingsFormSchema),
		defaultValues: async () => {
			const response = await client.settings.index.$get({
				query,
			});
			const json = await responseHandler(response);
			return json.settings as ProjectSettingsFormSchema;
		},
	});

	const { t } = useTranslation();
	const { citadel } = useCitadel();

	/** Computed */
	// Reading the settings is open — every surface that shows a billing date needs them — but the
	// write is gated on `update Organization`, which an ordinary employee does not hold. This form
	// saves on change and has no submit button, so a caller without the rule reads the values and
	// cannot touch them; leaving the inputs live would toast a 403 half a second after every
	// keystroke. Rules still loading leave them live and let the server answer.
	const canUpdate = citadel ? citadel.can("update", "Organization") : true;

	useAutoSave(methods, async (json: ProjectSettingsFormSchema) => {
		try {
			const response = await client.settings.index.$patch({
				query,
				json,
			});
			await responseHandler(response);
			toast.success(t("general.saved"));
		} catch (error) {
			console.error(error);
		}
	});

	/** Render */
	return (
		<FormProvider {...methods}>
			<ContentCard
				title={t("projects.project_settings")}
				icon={icon("Settings")}
			>
				<SettingsItem
					title={t("projects.invoicing_period")}
					description={t("projects.invoicing_period_description")}
				>
					<div className="h-stack">
						<TextInput
							type="number"
							className="input"
							min={0}
							control={methods.control}
							name="invoicingPeriodValue"
							label={undefined}
							isRequired
							placeholder="0"
							isDisabled={!canUpdate}
						/>
						<Select
							label={undefined}
							control={methods.control}
							isRequired
							name="invoicingPeriodUnit"
							itemNotFoundLabel={undefined}
							options={dayUnits.map((unit) => ({
								label: t(getUnitTranslationKey(unit)),
								value: unit,
								item: null,
							}))}
							isNullable={false}
							isDisabled={!canUpdate}
						/>
					</div>
				</SettingsItem>
			</ContentCard>
		</FormProvider>
	);
}
